/**
 * Chat History Sidebar.
 *
 * A right-side sheet that lists prior conversations for the current agent.
 * Allows switching between conversations and starting new chats.
 */

import React from 'react';
import { MessageSquare, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { ConversationSummary } from '@/types/chat-agent-types';

export interface ChatHistorySidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onNewChat: () => void;
  isLoading?: boolean;
}

/** Format a date string into a short relative/absolute label. */
function formatTimestamp(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

export const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  open,
  onOpenChange,
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewChat,
  isLoading = false,
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='w-80 sm:w-80 p-0 flex flex-col'>
        <SheetHeader className='px-4 pr-12 py-4 border-b'>
          <div className='flex items-center justify-between'>
            <SheetTitle className='text-sm font-semibold'>
              Chat History
            </SheetTitle>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                onNewChat();
                onOpenChange(false);
              }}
              className='gap-1.5 h-7 text-xs'
            >
              <Plus className='size-3.5' />
              New Chat
            </Button>
          </div>
        </SheetHeader>

        <div className='flex-1 overflow-y-auto'>
          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <span className='text-sm text-muted-foreground animate-pulse'>
                Loading conversations...
              </span>
            </div>
          ) : conversations.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 px-4 text-center'>
              <MessageSquare className='h-8 w-8 text-muted-foreground/40 mb-2' />
              <p className='text-sm text-muted-foreground'>
                No conversations yet
              </p>
              <p className='text-xs text-muted-foreground/60 mt-1'>
                Start chatting to see your history here
              </p>
            </div>
          ) : (
            <ul className='py-1'>
              {conversations.map((convo) => {
                const isActive = activeConversationId === convo.conversationId;
                return (
                  <li key={convo.conversationId}>
                    <button
                      type='button'
                      onClick={() => {
                        onSelectConversation(convo.conversationId);
                        onOpenChange(false);
                      }}
                      className={cn(
                        'w-full text-left px-4 py-2.5 transition-colors',
                        'hover:bg-muted/50',
                        isActive && 'bg-primary/5 border-l-2 border-primary',
                      )}
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <span
                          className={cn(
                            'text-sm truncate flex-1',
                            isActive
                              ? 'font-medium text-primary'
                              : 'text-foreground/80',
                          )}
                        >
                          {convo.title || 'Untitled'}
                        </span>
                        <span className='text-[10px] text-muted-foreground whitespace-nowrap mt-0.5'>
                          {formatTimestamp(convo.updatedAt || convo.createdAt)}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ChatHistorySidebar;
